/**
 * CPAYMENT-1731: короткий звуковой сигнал к попапу «мало времени».
 *
 * Синтезируем сигнал через WebAudio вместо аудио-файла: попапу нужен короткий «обрати внимание»,
 * а не мелодия, и так не появляется ни бинарника в репозитории, ни отдельного сетевого запроса
 * ровно в тот момент, когда у клиента и так истекает время.
 *
 * **Контекст обязан быть разбужен пользовательским жестом заранее.** Попап открывается по таймеру,
 * то есть вне обработчика события, а `AudioContext`, созданный в такой момент на не тронутой
 * странице, рождается `suspended`: его часы стоят, `resume()` не резолвится и не отклоняется —
 * просто виснет, и звук не играет никогда. Замерено в Chrome на изолированном стенде: без единого
 * клика по документу `state = suspended`, `currentTime = 0`; после любого клика тот же код играет.
 * Поэтому контекст один на страницу, будится первым касанием (`primeAlertSound`) и живёт до конца
 * сессии — закрывать его нельзя, иначе следующий сигнал снова упрётся в политику автовоспроизведения.
 *
 * Если клиент не коснулся страницы ни разу, звука не будет ни в одном браузере — это гарантия
 * платформы, обойти её нельзя. Попап в этом случае показывается молча, UI не ломается.
 */

/**
 * Сигнал подобран на слух (CPAYMENT-1731): три удара **одной** ноты G4 с колокольным затуханием.
 * Без мелодии — три одинаковых удара читаются как «обрати внимание», а не как «что-то случилось»,
 * и не спорят со звуками самого мерчанта. Октавный призвук поверх основного тона даёт колокольный
 * характер, низкая G4 (392 Гц) звучит теплее и глуше стеклянной синусоиды в верхнем регистре.
 *
 * Числа менять только вместе с прослушиванием — на глаз тональность не подбирается.
 */
const NOTE_HZ = 392;      // G4 — основной тон
const OVERTONE_HZ = 784;  // октава выше — колокольный призвук
const STRIKES = 3;
const SPACING_SEC = 0.13;

/** Затухание удара; последний длиннее — на нём сигнал «закрывается». */
const DECAY_SEC = 0.264;
const LAST_DECAY_SEC = 0.528;
/** Призвук гаснет быстрее основного тона, иначе звенит дольше самого удара. */
const OVERTONE_DECAY_RATIO = 0.6;

/**
 * Уровень подобран на слух: базовые пики 0.17 / 0.19 / 0.10 при общей громкости 160%.
 *
 * Взято с запасом сознательно — у клиента системная громкость может стоять низко, а наш уровень
 * на неё умножается. Потолок без искажений — около 0.6 на тон (на каждом ударе звучат основной
 * тон и призвук, их амплитуды складываются), так что до перегрузки ещё далеко.
 */
const PEAK_GAIN = 0.272;
const LAST_PEAK_GAIN = 0.304;
const OVERTONE_PEAK_GAIN = 0.16;

type Tone = {
  frequency: number;
  startAt: number;
  duration: number;
  peak: number;
};

const buildTones = (): Tone[] => {
  const tones: Tone[] = [];

  for (let i = 0; i < STRIKES; i += 1) {
    const isLast = i === STRIKES - 1;
    const startAt = i * SPACING_SEC;
    const duration = isLast ? LAST_DECAY_SEC : DECAY_SEC;

    tones.push({
      frequency: NOTE_HZ,
      startAt,
      duration,
      peak: isLast ? LAST_PEAK_GAIN : PEAK_GAIN,
    });
    tones.push({
      frequency: OVERTONE_HZ,
      startAt,
      duration: duration * OVERTONE_DECAY_RATIO,
      peak: OVERTONE_PEAK_GAIN,
    });
  }

  return tones;
};

const TONES = buildTones();

/** Любое из этих событий считается касанием страницы и будит звук. */
const UNLOCK_EVENTS = ['pointerdown', 'keydown', 'touchend'];

let sharedContext: AudioContext | null = null;
let unlockBound = false;

const createContext = (): AudioContext | null => {
  const AudioContextCtor =
    (window as any).AudioContext || (window as any).webkitAudioContext;
  if (!AudioContextCtor) return null;

  try {
    return new AudioContextCtor();
  } catch {
    return null;
  }
};

const getContext = (): AudioContext | null => {
  if (!sharedContext) sharedContext = createContext();
  return sharedContext;
};

/**
 * Разбудить звук на первом же касании страницы.
 *
 * Вызывается один раз при монтировании страницы. Сам жест ничего не проигрывает — он лишь
 * переводит общий контекст в `running`, чтобы попап, который откроется минутами позже уже без
 * жеста, успел прозвучать.
 *
 * Пустой буфер внутри обработчика — требование WebKit: там мало создать контекст в момент жеста,
 * через него нужно что-то реально проиграть, иначе он остаётся заглушенным.
 */
export const primeAlertSound = (): void => {
  if (unlockBound) return;
  unlockBound = true;

  const unlock = () => {
    UNLOCK_EVENTS.forEach((event) =>
      window.removeEventListener(event, unlock, true),
    );

    try {
      const ctx = getContext();
      if (!ctx) return;

      Promise.resolve(ctx.resume?.()).catch(() => {});

      const source = ctx.createBufferSource();
      source.buffer = ctx.createBuffer(1, 1, 22050);
      source.connect(ctx.destination);
      source.start(0);
    } catch {
      // звук никогда не ломает страницу оплаты
    }
  };

  UNLOCK_EVENTS.forEach((event) =>
    window.addEventListener(event, unlock, { capture: true, passive: true }),
  );
};

const scheduleTones = (ctx: AudioContext): void => {
  TONES.forEach(({ frequency, startAt, duration, peak }) => {
    const oscillator = ctx.createOscillator();
    const gain = ctx.createGain();

    oscillator.type = 'sine';
    oscillator.frequency.value = frequency;

    const start = ctx.currentTime + startAt;
    const stop = start + duration;

    // Быстрая атака и экспоненциальный спад дают удар с затуханием, как у колокола. Резкий
    // старт или обрыв звучал бы щелчком — как поломка, а не как сигнал.
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(peak, start + 0.015);
    gain.gain.exponentialRampToValueAtTime(0.0001, stop);

    oscillator.connect(gain);
    gain.connect(ctx.destination);
    oscillator.start(start);
    // Небольшой запас после спада: обрывать осциллятор ровно в точке нуля незачем.
    oscillator.stop(stop + 0.02);
  });
};

export const playAlertSound = (): void => {
  try {
    const ctx = getContext();
    if (!ctx) return;

    // Контекст мог не успеть проснуться (жест был прямо сейчас) либо не просыпаться вовсе
    // (страницу не трогали). Планируем тоны только после успешного resume: на спящем контексте
    // часы стоят, и `currentTime` дал бы бессмысленное расписание.
    if (ctx.state === 'suspended') {
      Promise.resolve(ctx.resume?.())
        .then(() => scheduleTones(ctx))
        .catch(() => {});
      return;
    }

    scheduleTones(ctx);
  } catch {
    // звук никогда не ломает страницу оплаты
  }
};
